import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, Badge, Spinner } from "@vlab-corporation/design-system";
import { AppLayout } from "@/components/AppLayout";
import { MemberTypeBadge } from "@/components/MemberTypeBadge";
import { ClassTypeBadge } from "@/components/ClassFormatSelector";
import { PurchaseTicketModal } from "@/components/PurchaseTicketModal";
import { EditMemberModal } from "@/components/EditMemberModal";
import { useToast } from "@/components/ToastProvider";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { useDeleteMember } from "@/hooks/useCreateMember";
import { getMembersList, getReRegistrations } from "@/services/firestore/members";
import {
  getMemberTickets,
  useCancelMemberTicket,
  type MemberTicket,
} from "@/hooks/useTicketPurchase";
import { isDemoMode } from "@/demo/demoMode";
import { getJournalsByMemberId } from "@/services/firestore/journals";
import type { Journal } from "@vlab-corporation/vlab-pilates-service-interface/class";

/** 회원 삭제·수강권 환불 권한 = 백엔드 requireAdmin과 동일 기준(users/{uid}.role). */
function isCenterManager(role: string | undefined): boolean {
  return role === "admin" || role === "owner";
}

function formatDate(dateStr: string | unknown): string {
  if (typeof dateStr !== "string") return "";
  return dateStr.substring(0, 10);
}

/** ISO 8601 만료일을 "YYYY-MM-DD"로, 없으면 "무제한" */
function formatExpireAt(expireAt: string | undefined): string {
  if (!expireAt) return "무제한";
  return expireAt.substring(0, 10);
}

function TicketCard({ ticket, onCancel }: { ticket: MemberTicket; onCancel?: () => void }) {
  return (
    <Card className="p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-medium text-gray-800 truncate">{ticket.title}</span>
          <Badge variant="info">{ticket.type === "P" ? "프라이빗" : "그룹"}</Badge>
          {!ticket.isActive && <Badge variant="default">만료</Badge>}
        </div>
        <span className="text-sm text-gray-600 flex-shrink-0">
          {ticket.remainingCoupon}/{ticket.maxCoupon}회
        </span>
      </div>
      <p className="text-xs text-gray-400 mt-1">만료: {formatExpireAt(ticket.expireAt)}</p>
      {onCancel && ticket.isActive && (
        <div className="mt-2 flex justify-end">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            환불·취소
          </Button>
        </div>
      )}
    </Card>
  );
}

function JournalCard({ journal }: { journal: Journal }) {
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <ClassTypeBadge classType={journal.classType} isOt={journal.isOt ?? false} />
          <span className="text-xs text-gray-400">{formatDate(journal.createdAt)}</span>
        </div>
        <span className="text-xs text-gray-400">#{journal.classId}</span>
      </div>
      {journal.problemList && (
        <p className="text-sm text-gray-700 line-clamp-2">{journal.problemList}</p>
      )}
      {journal.improvements && (
        <p className="text-xs text-gray-500 mt-1 line-clamp-1">개선: {journal.improvements}</p>
      )}
    </Card>
  );
}

export function MemberDetailPage() {
  const { memberId } = useParams<{ memberId: string }>();
  const navigate = useNavigate();
  const user = useAuthenticatedUser();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [isPurchaseOpen, setIsPurchaseOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<{ ticket: MemberTicket; index: number } | null>(
    null,
  );

  const centerOwnerUid = user?.activeMembership.centerOwnerUid ?? "";
  const centerId = user?.activeMembership.centerId ?? 0;
  const centerName = user?.activeMembership.centerName ?? "";
  const instructorId = user?.activeMembership.instructorId ?? "";
  const canManage = isCenterManager(user?.role);
  const enabled = !!user && !!memberId;

  const numericMemberId = memberId ? Number(memberId) : NaN;
  const ticketsEnabled = enabled && !isNaN(numericMemberId);

  const deleteMember = useDeleteMember();
  const cancelMemberTicket = useCancelMemberTicket();

  const {
    data: tickets,
    isLoading: ticketsLoading,
    error: ticketsError,
    refetch: refetchTickets,
  } = useQuery({
    queryKey: ["memberTickets", centerOwnerUid, centerId, memberId],
    queryFn: () => getMemberTickets(centerOwnerUid, centerId, numericMemberId),
    staleTime: 5 * 60 * 1000,
    enabled: ticketsEnabled,
  });

  // 회원 목록에서 해당 회원 찾기
  const { data: members } = useQuery({
    queryKey: ["membersList", centerOwnerUid, centerId],
    queryFn: () => getMembersList(centerOwnerUid, centerId),
    staleTime: 30 * 60 * 1000,
    enabled,
  });

  const { data: reRegistrations } = useQuery({
    queryKey: ["reRegistrations", centerOwnerUid, centerId],
    queryFn: () => getReRegistrations(centerOwnerUid, centerId),
    staleTime: 30 * 60 * 1000,
    enabled,
  });

  const member = members?.find((m) => m.id === memberId);

  const studiomateMemberId = memberId ? Number(memberId) : undefined;
  const memberName = member?.name;

  const { data: journals, isLoading: journalsLoading } = useQuery({
    queryKey: ["memberJournals", centerOwnerUid, centerId, memberId, memberName],
    queryFn: () => getJournalsByMemberId(centerOwnerUid, centerId, studiomateMemberId!, memberName),
    staleTime: 5 * 60 * 1000,
    enabled: enabled && studiomateMemberId !== undefined && !isNaN(studiomateMemberId),
  });

  if (!user) return null;

  const handleDeleteClick = () => {
    if (canManage) setIsDeleteOpen(true);
    else setIsContactOpen(true);
  };

  const handleCancelClick = (ticket: MemberTicket, index: number) => {
    if (canManage) setCancelTarget({ ticket, index });
    else setIsContactOpen(true);
  };

  const handleConfirmDelete = () => {
    deleteMember.mutate(
      { centerId, memberId: numericMemberId },
      {
        onSuccess: () => {
          setIsDeleteOpen(false);
          toast.show("회원이 삭제되었습니다.", "success");
          void queryClient.invalidateQueries({
            queryKey: ["membersList", centerOwnerUid, centerId],
          });
          navigate("/members");
        },
        onError: () => {
          toast.show("회원 삭제에 실패했습니다.", "error");
        },
      },
    );
  };

  const handleConfirmCancel = () => {
    if (!cancelTarget) return;
    cancelMemberTicket.mutate(
      {
        centerId,
        memberId: numericMemberId,
        ticketIndex: cancelTarget.index,
        expectedTitle: cancelTarget.ticket.title,
        expectedPurchasedAt: cancelTarget.ticket.purchasedAt,
      },
      {
        onSuccess: () => {
          setCancelTarget(null);
          void refetchTickets();
          toast.show("수강권이 취소되었습니다.", "success");
        },
        onError: () => {
          toast.show("수강권 취소에 실패했습니다.", "error");
        },
      },
    );
  };

  return (
    <AppLayout>
      <div className="mb-4">
        <Button variant="ghost" onClick={() => navigate("/members")}>
          ← 회원 목록
        </Button>
      </div>

      {/* 회원 정보 */}
      {member ? (
        <Card className="p-4 mb-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold mb-2">{member.name}</h2>
              <div className="flex items-center gap-2 flex-wrap">
                <MemberTypeBadge
                  member={member}
                  instructorId={instructorId}
                  reRegistrations={reRegistrations}
                />
                {member.contact && <span className="text-sm text-gray-500">{member.contact}</span>}
              </div>
            </div>
            {!isDemoMode() && (
              <div className="flex flex-shrink-0 gap-2">
                <Button variant="outline" size="sm" onClick={() => setIsEditOpen(true)}>
                  수정
                </Button>
                <Button variant="ghost" size="sm" onClick={handleDeleteClick}>
                  삭제
                </Button>
              </div>
            )}
          </div>
        </Card>
      ) : (
        <Card className="p-4 mb-4">
          <p className="text-sm text-gray-500">회원 정보를 찾을 수 없습니다.</p>
        </Card>
      )}

      {/* 보유 수강권 (회원 정보가 있을 때만 표시) */}
      {member && (
        <>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700">보유 수강권</h3>
            {!isDemoMode() && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsPurchaseOpen(true)}
                disabled={!member.contact}
                title={
                  !member.contact ? "연락처가 없는 회원은 수강권을 등록할 수 없습니다." : undefined
                }
              >
                수강권 구매
              </Button>
            )}
          </div>

          {!member.contact && !isDemoMode() && (
            <p className="text-xs text-gray-400 mb-2">
              연락처가 없는 회원은 수강권 등록이 불가합니다. 관리자에게 연락처 보강을 요청하세요.
            </p>
          )}

          {ticketsLoading ? (
            <div className="flex justify-center py-6">
              <Spinner size="md" />
            </div>
          ) : ticketsError ? (
            <Card className="p-4 text-center mb-6">
              <p className="text-sm text-red-600">수강권 정보를 불러오지 못했습니다.</p>
              <Button variant="outline" size="sm" onClick={() => refetchTickets()} className="mt-2">
                다시 시도
              </Button>
            </Card>
          ) : !tickets || tickets.length === 0 ? (
            <Card className="p-4 text-center mb-6">
              <p className="text-sm text-gray-500">보유한 수강권이 없습니다.</p>
            </Card>
          ) : (
            <div className="space-y-2 mb-6">
              {tickets.map((ticket, idx) => (
                <TicketCard
                  key={`${ticket.title}-${ticket.purchasedAt ?? "n/a"}-${idx}`}
                  ticket={ticket}
                  onCancel={isDemoMode() ? undefined : () => handleCancelClick(ticket, idx)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* 일지 목록 */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700">수업 일지</h3>
        {journals && journals.length > 0 && <Badge variant="info">{journals.length}건</Badge>}
      </div>

      {journalsLoading ? (
        <div className="flex justify-center py-8">
          <Spinner size="lg" />
        </div>
      ) : !journals || journals.length === 0 ? (
        <Card className="p-6 text-center">
          <p className="text-sm text-gray-500 mb-1">작성된 일지가 없습니다.</p>
          <p className="text-xs text-gray-400">
            이전에 저장된 일지는 회원 연결 정보가 누락되어 표시되지 않을 수 있습니다.
          </p>
        </Card>
      ) : (
        <div className="space-y-2">
          {journals.map((journal) => (
            <JournalCard key={journal.id} journal={journal} />
          ))}
        </div>
      )}

      {member && (
        <PurchaseTicketModal
          isOpen={isPurchaseOpen}
          ownerUid={centerOwnerUid}
          centerId={centerId}
          centerName={centerName}
          memberName={member.name}
          memberMobile={member.contact ?? ""}
          onClose={() => setIsPurchaseOpen(false)}
          onCreated={() => {
            setIsPurchaseOpen(false);
            void queryClient.invalidateQueries({
              queryKey: ["memberTickets", centerOwnerUid, centerId, memberId],
            });
            toast.show("수강권이 등록되었습니다.", "success");
          }}
        />
      )}

      {/* 회원 정보 수정 (강사 가능) */}
      {member && (
        <EditMemberModal
          isOpen={isEditOpen}
          centerId={centerId}
          memberId={numericMemberId}
          initialName={member.name}
          initialMobile={member.contact ?? ""}
          onClose={() => setIsEditOpen(false)}
          onUpdated={() => {
            setIsEditOpen(false);
            void queryClient.invalidateQueries({
              queryKey: ["membersList", centerOwnerUid, centerId],
            });
            toast.show("회원 정보가 수정되었습니다.", "success");
          }}
        />
      )}

      {/* 회원 삭제 확인 (owner/admin만 도달) */}
      {isDeleteOpen && member && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <Card className="w-full max-w-sm p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-2">회원 삭제</h3>
            <p className="text-sm text-gray-600 mb-5">"{member.name}" 회원을 삭제하시겠습니까?</p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setIsDeleteOpen(false)}
                disabled={deleteMember.isPending}
              >
                취소
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmDelete}
                disabled={deleteMember.isPending}
              >
                {deleteMember.isPending ? "삭제 중..." : "삭제"}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* 수강권 환불·취소 확인 (owner/admin만 도달) */}
      {cancelTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <Card className="w-full max-w-sm p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-2">수강권 환불·취소</h3>
            <p className="text-sm text-gray-600 mb-5">
              "{cancelTarget.ticket.title}" 수강권을 취소하시겠습니까?
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setCancelTarget(null)}
                disabled={cancelMemberTicket.isPending}
              >
                취소
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmCancel}
                disabled={cancelMemberTicket.isPending}
              >
                {cancelMemberTicket.isPending ? "처리 중..." : "확인"}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* 권한 안내 팝업 (instructor) */}
      {isContactOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <Card className="w-full max-w-sm p-6 text-center">
            <h3 className="text-base font-semibold text-gray-900 mb-2">권한 안내</h3>
            <p className="text-sm text-gray-600 mb-5">
              회원 삭제·수강권 환불은 원장·관리자 권한이 필요합니다.
              <br />
              원장에게 문의해주세요.
            </p>
            <Button variant="primary" onClick={() => setIsContactOpen(false)} className="w-full">
              확인
            </Button>
          </Card>
        </div>
      )}
    </AppLayout>
  );
}
